<template>
  <div>
    <!-- Dialog pour afficher tous les codes NAF -->
    <v-dialog v-model="nafCodesDialog" max-width="900px" scrollable>
      <v-card>
        <v-toolbar :color="$colors[0]" class="text-white" dark elevation="0">
          <v-icon left>mdi-alert-circle</v-icon>
          <span class="text-h6 font-weight-medium">
            Codes NAF - {{ selectedFournisseur }}</span
          >
          <v-spacer></v-spacer>
          <v-btn icon dark @click="closeDialogCodeNAF">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pt-4">
          <!-- Barre de recherche -->
          <v-text-field
            v-model="searchNafCode"
            label="Rechercher un code NAF"
            prepend-icon="mdi-magnify"
            clearable
            outlined
            dense
            hide-details
            class="mb-4"
          ></v-text-field>

          <!-- Affichage des codes NAF filtrés -->
          <div class="d-flex flex-wrap mt-3">
            <template v-if="filteredNafCodes.length > 0">
              <v-chip
                v-for="(code, idx) in filteredNafCodes"
                :key="idx"
                outlined
                class="mr-2 mb-2"
              >
                {{ code }}
              </v-chip>
            </template>
            <div v-else class="pa-3 text-center grey--text text--darken-1 w-100">
              Aucun code NAF ne correspond à votre recherche
            </div>
          </div>
        </v-card-text>

        <v-divider></v-divider>
        <v-card-actions>
          <div class="text-body-2 grey--text">
            {{ filteredNafCodes.length }} code(s) sur {{ displayedNafCodes.length }}
          </div>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="closeDialogCodeNAF"> Fermer </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-data-table
      :items-per-page="listDataAssureur.length"
      :headers="headers"
      :items="listDataAssureur"
      hide-default-footer
      :loading="loading"
      loading-text="Chargement des données..."
      class="elevation-0"
    >
      <template v-slot:loading>
        <v-skeleton-loader
          v-for="n in 5"
          :key="n"
          type="list-item-avatar-two-line"
          class="my-2"
        ></v-skeleton-loader>
      </template>

      <!-- Fournisseur avec badge si score élevé -->
      <template v-slot:item.fournisseur="{ item }">
        <div class="d-flex align-center">
          <span>{{ item.fournisseur }}</span>

          <v-icon
            v-if="
              (item.score >= 80 && item.assureur == 'CREDITSAFE') ||
              (item.score >= 8 && item.assureur == 'ELLIPRO')
            "
            color="green"
            class="ml-2"
            small
            >mdi-star-check-outline</v-icon
          >
        </div>
      </template>

      <!-- Assureur avec placeholder si vide -->
      <template v-slot:item.assureur="{ item }">
        <span v-if="!!item.assureur.trim()">{{ item.assureur }}</span>
        <span v-else class="grey--text font-italic">Non défini</span>
      </template>

      <!-- Score avec visualisation -->
      <template v-slot:item.score="{ item }">
        <div class="d-flex align-center">
          <v-progress-linear
            :value="normalizedScore(item.score, item.assureur)"
            height="8"
            rounded
            :color="getScoreColor(item.score, item.assureur)"
            class="mr-2"
            style="width: 60px"
          ></v-progress-linear>
          <span :class="getScoreTextColor(item.score, item.assureur)">{{
            item.score
          }}</span>
        </div>
      </template>

      <!-- NAF codes avec expansion -->
      <template v-slot:item.codeNaf="{ item, index }">
        <div style="max-width: 250px">
          <!-- Bouton pour ouvrir la dialog -->
          <v-tooltip
            bottom
            v-if="
              spliteListSlice(item.codeNaf).length > 0 &&
              !!item.codeNaf &&
              item.codeNaf != ','
            "
          >
            <template v-slot:activator="{ on, attrs }">
              <v-chip
                small
                outlined
                @click="showNafCodesDialog(item.codeNaf, item.fournisseur)"
              >
                +{{ spliteListSlice(item.codeNaf).length + 1 }}
                <v-icon right x-small>mdi-eye</v-icon>
              </v-chip>
            </template>
            <span>Voir tous les codes NAF</span>
          </v-tooltip>

          <div v-if="!item.codeNaf || item.codeNaf == ','" class="grey--text font-italic">
            Non défini
          </div>
        </div>
      </template>

      <!-- Date formatée -->
      <!-- Date début min -->
      <template v-slot:item.date_debut_min="{ item }">
        <v-chip
          v-if="isValidDate(item.date_debut_min)"
          small
          outlined
          :color="
            isPastDate(item.date_debut_min)
              ? 'green'
              : isDateNearing(item.date_debut_min)
              ? 'orange'
              : 'default'
          "
        >
          <v-icon left x-small v-if="isPastDate(item.date_debut_min)"
            >mdi-clock-check-outline</v-icon
          >
          <v-icon left x-small v-if="isDateNearing(item.date_debut_min)"
            >mdi-clock-alert-outline</v-icon
          >
          {{ formatDate(item.date_debut_min) }}
        </v-chip>
        <v-chip
          v-else-if="isRelativeDayOrMonth(item.date_debut_min)"
          small
          outlined
          color="indigo"
        >
          <v-icon left x-small>mdi-calendar-clock</v-icon>
          {{ item.date_debut_min }}
        </v-chip>
        
        <span v-else-if="item.date_debut_min === 'NA'" class="grey--text">NA</span>
        <span v-else class="grey--text font-italic">Non défini</span>
      </template>

      <!-- Date début max -->
      <template v-slot:item.date_debut_max="{ item }">
        <v-chip
          v-if="isValidDate(item.date_debut_max)"
          small
          outlined
          :color="
            isPastDate(item.date_debut_max)
              ? 'green'
              : isDateNearing(item.date_debut_max)
              ? 'orange'
              : 'default'
          "
        >
          <v-icon left x-small v-if="isPastDate(item.date_debut_max)"
            >mdi-clock-check-outline</v-icon
          >
          <v-icon left x-small v-if="isDateNearing(item.date_debut_max)"
            >mdi-clock-alert-outline</v-icon
          >
          {{ formatDate(item.date_debut_max) }}
        </v-chip>
        <v-chip
          v-else-if="isRelativeDayOrMonth(item.date_debut_max)"
          small
          outlined
          color="indigo"
        >
          <v-icon left x-small>mdi-calendar-clock</v-icon>
          {{ item.date_debut_max }}
        </v-chip>
        <span v-else-if="item.date_debut_max === 'NA'" class="grey--text">NA</span>
        <span v-else class="grey--text font-italic">Non défini</span>
      </template>

      <!-- Date fin min -->
      <template v-slot:item.date_fin_min="{ item }">
        <v-chip
          v-if="isValidDate(item.date_fin_min)"
          small
          outlined
          :color="
            isPastDate(item.date_fin_min)
              ? 'green'
              : isDateNearing(item.date_fin_min)
              ? 'orange'
              : 'default'
          "
        >
          <v-icon left x-small v-if="isPastDate(item.date_fin_min)"
            >mdi-clock-check-outline</v-icon
          >
          <v-icon left x-small v-if="isDateNearing(item.date_fin_min)"
            >mdi-clock-alert-outline</v-icon
          >
          {{ formatDate(item.date_fin_min) }}
        </v-chip>
        <v-chip
          v-else-if="isRelativeDayOrMonth(item.date_fin_min)"
          small
          outlined
          color="indigo"
        >
          <v-icon left x-small>mdi-calendar-clock</v-icon>
          {{ item.date_fin_min }}
        </v-chip>
        <span v-else-if="item.date_fin_min === 'NA'" class="grey--text">NA</span>
        <span v-else class="grey--text font-italic">Non défini</span>
      </template>

      <!-- Date fin max -->
      <template v-slot:item.date_fin_max="{ item }">
        <v-chip
          v-if="isValidDate(item.date_fin_max)"
          small
          outlined
          :color="
            isPastDate(item.date_fin_max)
              ? 'green'
              : isDateNearing(item.date_fin_max)
              ? 'orange'
              : 'default'
          "
        >
          <v-icon left x-small v-if="isPastDate(item.date_fin_max)"
            >mdi-clock-check-outline</v-icon
          >
          <v-icon left x-small v-if="isDateNearing(item.date_fin_max)"
            >mdi-clock-alert-outline</v-icon
          >
          {{ formatDate(item.date_fin_max) }}
        </v-chip>
        <v-chip
          v-else-if="isRelativeDayOrMonth(item.date_fin_max)"
          small
          outlined
          color="indigo"
        >
          <v-icon left x-small>mdi-calendar-clock</v-icon>
          {{ item.date_fin_max }}
        </v-chip>
        <span v-else-if="item.date_fin_max === 'NA'" class="grey--text">NA</span>
        <span v-else class="grey--text font-italic">Non défini</span>
      </template>
      <!-- Marge min avec formatage et couleur -->
      <template v-slot:item.min_marge="{ item }">
        <div class="d-flex align-center">
          <v-chip small outlined :color="getMargeColor(item.min_marge)">
            {{ item.min_marge }} €
          </v-chip>
        </div>
      </template>

      <!-- Marge max avec formatage et couleur -->
      <template v-slot:item.max_marge="{ item }">
        <div class="d-flex align-center">
          <v-chip small outlined :color="getMargeColor(item.max_marge)">
            {{ item.max_marge }} €
          </v-chip>
        </div>
      </template>

      <!-- Consommation min avec formatage et indicateur visuel -->
      <template v-slot:item.min_conso="{ item }">
        <div class="d-flex align-center">
          <v-chip small outlined :color="getConsoColor(item.min_conso)">
            <v-icon left x-small>mdi-flash</v-icon>
            {{ item.min_conso }} KWh
          </v-chip>
        </div>
      </template>

      <!-- Consommation max avec formatage et indicateur visuel -->
      <template v-slot:item.max_conso="{ item }">
        <div class="d-flex align-center">
          <v-chip small outlined :color="getConsoColor(item.max_conso)">
            <v-icon left x-small>mdi-flash</v-icon>
            {{ item.max_conso }} KWh
          </v-chip>
        </div>
      </template>

      <!-- Consommation été avec formatage et indicateur saisonnier -->
      <template v-slot:item.consoEte="{ item }">
        <div class="d-flex align-center">
          <v-chip small outlined color="amber">
            <v-icon left x-small>mdi-weather-sunny</v-icon>
            {{ item.consoEte }} KWh
          </v-chip>
        </div>
      </template>
      <!-- Actions -->
      <template v-slot:item.actions="{ item }">
        <div class="d-flex">
          <v-btn icon small class="mr-1" @click="editItem(item)" color="primary">
            <v-icon small>mdi-pencil</v-icon>
          </v-btn>
          <v-btn icon small @click="deleteItem(item)" color="red darken-1">
            <v-icon small>mdi-delete</v-icon>
          </v-btn>
        </div>
      </template>

      <!-- Empty state -->
      <template v-slot:no-data>
        <div class="d-flex flex-column align-center py-5">
          <v-icon size="64" color="grey lighten-1">mdi-database-off</v-icon>
          <div class="text-subtitle-1 mt-3 grey--text text--darken-1">
            Aucune donnée disponible
          </div>
          <v-btn :color="$colors[0]" text class="mt-2" @click="dialog = true">
            Ajouter un fournisseur
          </v-btn>
        </div>
      </template>
    </v-data-table>
    <!-- Pagination améliorée -->
    <div class="px-6 py-3 d-flex justify-center" v-if="listDataAssureur.length > 0">
      <pagination
        :pagination="pagination"
        @selectNumberOfRecords="rechercheData"
        @first="getDataAssureur(pagination.firstPage)"
        @last="getDataAssureur(pagination.lastPage)"
        @prev="getDataAssureur(pagination.prevPageUrl)"
        @current="getDataAssureur(pagination.currentPage)"
        @next="getDataAssureur(pagination.nextPageUrl)"
      />
    </div>
  </div>
</template>
<script>
import axios from "axios";
import Pagination from "../../../../components/dataTable/Pagination.vue";
export default {
  components: {
    pagination: Pagination,
  },
  props: {
    listDataAssureurModif: Boolean,
  },
  data() {
    return {
      searchNafCode: "",
      nafCodesDialog: false,
      displayedNafCodes: [],
      selectedFournisseur: "",
      loading: false,
      expandedItems: [],
      tableData: {
        statusFilter: "",
        draw: 0,
        length: 15,
        search: "",
        column: 0,
        dir: "asc",
      },
      pagination: {
        perPage: ["15", "20", "30"],
        firstPage: 1,
        lastPage: "",
        currentPage: 1,
        total: "",
        lastPageUrl: "",
        nextPageUrl: "",
        prevPageUrl: "",
        from: "",
        to: "",
      },
      listDataAssureur: [],
      headers: [
        { text: "Fournisseur", value: "fournisseur" },
        { text: "Assureur", value: "assureur" },
        { text: "Debut min", value: "date_debut_min" },
        { text: "Debut max", value: "date_debut_max" },
        { text: "Fin min", value: "date_fin_min" },
        { text: "Fin max", value: "date_fin_max" },
        { text: "Score", value: "score" },
        { text: "NAF", value: "codeNaf" },
        { text: "Marge min", value: "min_marge" },
        { text: "Marge max", value: "max_marge" },
        { text: "Conso min", value: "min_conso" },
        { text: "Conso max", value: "max_conso" },
        { text: "Conso ÉTÉ", value: "consoEte" },
        { text: "Actions", value: "actions" },
      ],
    };
  },
  watch: {
    listDataAssureurModif: async function (newVal) {
      if (newVal) {
        await this.getDataAssureur(this.pagination.currentPage);
        this.$emit("listDataAssureurModif", false);
      }
    },
  },
  computed: {
    filteredNafCodes() {
      if (!this.searchNafCode) {
        return this.displayedNafCodes;
      }

      const searchTerm = this.searchNafCode.toLowerCase();
      return this.displayedNafCodes.filter((code) =>
        code.toLowerCase().includes(searchTerm)
      );
    },
  },
  async mounted() {
    await this.getDataAssureur(this.pagination.currentPage);
  },
  methods: {
    normalizedScore(score, assureur) {
      if (assureur == "ELLIPRO") {
        return (score / 10) * 100;
      } else if (assureur == "CREDITSAFE") {
        return (score / 100) * 100;
      }
    },
    closeDialogCodeNAF() {
      this.nafCodesDialog = false;
      this.searchNafCode = "";
    },
    showNafCodesDialog(codesString, fournisseur) {
      // Récupérer tous les codes NAF
      this.displayedNafCodes = this.spliteList(codesString);
      this.selectedFournisseur = fournisseur;
      this.nafCodesDialog = true;
      console.log(this.nafCodesDialog, this.displayedNafCodes);
    },
    editItem(item) {
      this.$emit("editItem", item);
    },

    deleteItem(item) {
      this.$emit("deleteItem", item);
    },

    async rechercheData({ type, key }) {
      if (type == "search") {
        this.tableData.search = key;
      } else if (type == "nbrRecords") {
        this.tableData.length = key;
      }
      this.tableData.draw++;
      await axios({
        url: process.env.VUE_APP_URL_API_ADMIN + "getAllAssureurDatatable/",
        method: "POST",
        data: {
          dataTable: this.tableData,
          pageNumber: this.pagination.currentPage,
          token: this.getToken("dash_token"),
        },
      })
        .then((response) => {
          if (response.data.resultat.result == true) {
            let data = response.data.resultat.data.data;
            this.listDataAssureur = data.data;
            let pagination = response.data.resultat.data.data;
            this.configPagination(pagination);
          } else {
            alert("permission refusée");
            localStorage.removeItem("vendeurName");
            localStorage.removeItem("typeUser");
            localStorage.removeItem("token");
            this.$router.push("/login");
          }
        })
        .catch((errors) => {
          console.log(errors);
        });
    },
    expandItem(index) {
      if (this.expandedItems.includes(index)) {
        this.expandedItems = this.expandedItems.filter((i) => i !== index);
      } else {
        this.expandedItems.push(index);
      }
    },
    spliteList(str) {
      if (!str) return [];
      return str.split(",").filter((code) => code.trim() !== "");
    },

    // Méthode pour obtenir les codes supplémentaires (après le premier)
    spliteListSlice(str) {
      if (!str) return [];
      const codes = this.spliteList(str);
      return codes.length > 1 ? codes.slice(1) : [];
    },
    configPagination(pagination) {
      this.pagination.lastPage = pagination.last_page;
      this.pagination.currentPage = pagination.current_page;
      this.pagination.total = pagination.total;
      this.pagination.lastPageUrl = pagination.last_page_url;
      this.pagination.nextPageUrl = pagination.next_page_url;
      this.pagination.prevPageUrl = pagination.prev_page_url;
      this.pagination.from = pagination.from;
      this.pagination.to = pagination.to;
    },
    async getDataAssureur(pageNumber = 0) {
      let data = {
        token: this.getToken("dash_token"),
        dataTable: this.tableData,
        pageNumber: pageNumber,
      };
      await axios({
        url: process.env.VUE_APP_URL_API_ADMIN + "getAllAssureurDatatable/",
        method: "POST",
        data: data,
      }).then((res) => {
        this.listDataAssureur = res.data.resultat.data.data.data;
        let pagination = res.data.resultat.data.data;
        this.configPagination(pagination);
      });
      this.$emit("listDataAssureur", this.listDataAssureur);
    },
    formatDate(dateStr) {
      if (!dateStr || dateStr === "NA" || !this.isValidDate(dateStr)) return dateStr;

      const date = new Date(dateStr);
      return new Intl.DateTimeFormat("fr-FR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      }).format(date);
    },

    isValidDate(dateStr) {
      if (!dateStr || dateStr === "NA" || this.isRelativeDayOrMonth(dateStr)) return false;

      // Vérifier si c'est une date valide
      const date = new Date(dateStr);
      return !isNaN(date.getTime());
    },
    isRelativeDay(value){
      return typeof value === "string" && /^J\+\d+$/.test(value);
    },
    isRelativeDayOrMonth(value) {
      // Vérifier si la valeur correspond au format M+XX (ex: M+24)
      return typeof value === "string" && (/^M\+\d+$/.test(value) || /^J\+\d+$/.test(value));
    },
    isPastDate(dateStr) {
      if (!this.isValidDate(dateStr)) return false;

      const date = new Date(dateStr);
      const today = new Date();
      return date < today; // Retourne true si la date est dans le passé
    },

    isDateNearing(dateStr) {
      if (!this.isValidDate(dateStr)) return false;

      const date = new Date(dateStr);
      const today = new Date();
      const diffTime = date.getTime() - today.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 3600 * 24));
      return diffDays > 0 && diffDays <= 30; // Si la date est dans les 30 prochains jours
    },

    getScoreColor(score, assureur) {
      if (
        (score >= 80 && assureur == "CREDITSAFE") ||
        (score >= 8 && assureur == "ELLIPRO")
      )
        return "success";
      if (
        (score >= 50) & (assureur == "CREDITSAFE") ||
        (score >= 5 && assureur == "ELLIPRO")
      )
        return "warning";
      return "error";
    },

    getScoreTextColor(score, assureur) {
      if (
        (score >= 80 && assureur == "CREDITSAFE") ||
        (score >= 8 && assureur == "ELLIPRO")
      )
        return "success--text";
      if (
        (score >= 50) & (assureur == "CREDITSAFE") ||
        (score >= 5 && assureur == "ELLIPRO")
      )
        return "warning--text";
      return "error--text";
    },
    getMargeColor(marge) {
      const value = parseFloat(marge);
      if (isNaN(value)) return "default";

      if (value >= 100) return "success";
      if (value >= 15) return "primary";
      if (value >= 5) return "info";
      return "default";
    },

    getConsoColor(conso) {
      const value = parseFloat(conso);
      if (isNaN(value)) return "default";

      if (value > 36) return "error";
      if (value > 24) return "warning";
      if (value > 6) return "info";
      return "success";
    },
    deepEqual(obj1, obj2) {
      if (obj1 === obj2) return true;

      if (
        obj1 == null ||
        typeof obj1 != "object" ||
        obj2 == null ||
        typeof obj2 != "object"
      )
        return false;

      let keysA = Object.keys(obj1),
        keysB = Object.keys(obj2);
      if (keysA.length !== keysB.length) return false;

      for (let key of keysA) {
        if (!keysB.includes(key) || !deepEqual(obj1[key], obj2[key])) return false;
      }

      return true;
    },
  },
};
</script>
